import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {FeatureTogglesService, UsageType} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Address} from '@spartacus/core';
import {ComboBoxComponent, ComboBoxDropdownOptionComponent} from 'ng-design-system';
import {first} from 'rxjs/operators';
import {AddressesFormatterPipe} from '@app/pipes/addresses-formatter/addresses-formatter.pipe';
import {Subject} from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'dcp-company-addresses',
  templateUrl: './company-addresses.component.html',
  styleUrls: ['./company-addresses.component.scss']
})
export class CompanyAddressesComponent implements OnInit {
  @Input() addresses: Address[];
  @Input() header: string;
  @Input() initialSelectedShipToAddress: number = 0;
  @Input() addAddressModalLinkText: string;
  @Input() addAddressModalHeaderText: string;
  @Input() addAddressModalCreateButtonText: string;
  @Input() editAddressModalHeaderText: string;
  @Input() editAddressModalSubmitButtonText: string;
  @Input() addAddressModalUsageType: UsageType;
  @Input() actionButtonAlternativeText: string = '';
  @Input() showEditButton = false;
  @Output() selectedAddress: EventEmitter<Address> = new EventEmitter<Address>();
  @ViewChild('comboBox') comboBox: ComboBoxComponent;
  @ViewChildren('dropdownOptions') dropdownOptions: QueryList<ComboBoxDropdownOptionComponent>;

  selectedAddressData: string;
  openModal$: Subject<void> = new Subject();
  openEditModal$: Subject<void> = new Subject();
  addressToEdit: Address;
  isCreateNewLocationFeatureToggleEnabled: boolean;
  addressesEditableFeatureToggle: boolean;

  constructor(private addressesPipe: AddressesFormatterPipe,
              private cdr: ChangeDetectorRef,
              private featureTogglesService: FeatureTogglesService) {
  }

  ngOnInit(): void {
    this.subscribeToCreateNewLocationFeatureToggle();
    this.subscribeToCheckoutDataEditFeatureToggle();
    if (this.isSingleAddress()) {
      this.onAddressChange(this.addresses[0].sapAddressUUID || this.addresses[0].externalAddressNumber || this.addresses[0].id);
    }
  }

  isSingleAddress(): boolean {
    return this.addresses?.length === 1;
  }

  addNewAddress(address: Address): void {
    if (!this.addresses.includes(address)) {
      this.addresses.unshift(address);
      this.cdr.detectChanges();
    }
  }

  onAddressChange(addressIdentification: string): void {
    const selectedAddress: Address = this.addresses.find((address: Address) => address.sapAddressUUID === addressIdentification || address.externalAddressNumber === addressIdentification || address.id === addressIdentification);
    if (selectedAddress) {
      this.selectedAddress.emit(selectedAddress);
      this.selectedAddressData = this.addressesPipe.transform(selectedAddress);
      this.cdr.detectChanges();
    }
  }

  openModal(): void {
    this.openModal$.next();
  }

  openEditModal(): void {
    this.openEditModal$.next();
  }

  private subscribeToCreateNewLocationFeatureToggle() {
    this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.createNewLocation)
      .pipe(first(), untilDestroyed(this))
      .subscribe(enabled => this.isCreateNewLocationFeatureToggleEnabled = enabled);
  }

  private subscribeToCheckoutDataEditFeatureToggle() {
    this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.checkoutEditAddress)
      .pipe(first(), untilDestroyed(this))
      .subscribe(enabled => this.addressesEditableFeatureToggle = enabled);
  }

  updateAddress(updatedAddress: Address) {
    const updatedAddressIndex = this.addresses.findIndex(addr => addr.externalAddressNumber === updatedAddress.externalAddressNumber);
    this.addresses.splice(updatedAddressIndex, 1, updatedAddress);
    this.cdr.detectChanges();
    // for some reason when addresses array is modified with splice
    // then selected option is not selected anymore, so it has to be selected again with code
    const selectedDropdownOption = this.dropdownOptions.find(addr => addr.value === updatedAddress.externalAddressNumber);
    if (selectedDropdownOption) {
      this.comboBox.value = [selectedDropdownOption.value];
    }
    this.cdr.detectChanges();
  }

  getAddressId(address: Address): string {
    return address.sapAddressUUID || address.externalAddressNumber || address.id;
  }

  onEditClick(address: Address): void {
    this.addressToEdit = address;
    this.openEditModal();
  }
}
